import { useCallback, useEffect, useState } from 'react';
import getKato from '../../api/resources/getKato';
import styles from './SelectKato.module.css';

const SelectKato = ({ handleSetState, onChange, value, label, required, error, ...props }) => {
  const [katoTree, setKatoTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadKato = useCallback(() => {
    if (katoTree.length > 0) return;
    setLoading(true);
    getKato()
      .then((res) => {
        const tree = Array.isArray(res?.data) ? res.data : [];
        setKatoTree(tree);
      })
      .catch((err) => console.error('Error loading KATO:', err))
      .finally(() => setLoading(false));
  }, [katoTree.length]);

  const handleOpen = () => {
    setIsOpen(true);
    loadKato();
  };

  const toggleNode = (nodeId, e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleItemClick = (item) => {
    const hasChildren = item?.children && item.children.length > 0;

    if (hasChildren) {
      const newExpanded = new Set(expandedNodes);
      if (newExpanded.has(item.id)) {
        newExpanded.delete(item.id);
      } else {
        newExpanded.add(item.id);
      }
      setExpandedNodes(newExpanded);
      return;
    }

    setSelectedItem(item);
    if (onChange) {
      onChange(item.code);
    } else if (handleSetState) {
      handleSetState(item.code);
    }
    setIsOpen(false);
  };

  const findByCode = useCallback((nodes, code) => {
    const codeNum = Number(code);
    for (const node of nodes) {
      if (node.code === codeNum || node.code === code) return node;
      if (node.children) {
        const found = findByCode(node.children, code);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const getDisplayText = () => {
    if (selectedItem) {
      return selectedItem.name;
    }
    if (value && katoTree.length > 0) {
      const found = findByCode(katoTree, value);
      return found ? found.name : 'Ð ÐµÐ³Ð¸Ð¾Ð½ Ð²ÑÐ±ÑÐ°Ð½';
    }
    return '';
  };

  useEffect(() => {
    if (value && katoTree.length > 0 && !selectedItem) {
      const found = findByCode(katoTree, value);
      if (found) {
        setSelectedItem(found);
      }
    }
  }, [value, katoTree, selectedItem, findByCode]);

  useEffect(() => {
    if (value && katoTree.length === 0) {
      loadKato();
    }
  }, [value, katoTree.length, loadKato]);

  const TreeNode = ({ node, depth = 0 }) => {
    const hasChildren = node?.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedItem?.id === node.id || Number(value) === node.code || value === node.code;
    const canSelect = !hasChildren;

    let filteredChildren = node.children || [];
    if (searchQuery && hasChildren) {
      filteredChildren = filteredChildren.filter((child) =>
        child.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    const matchesSearch = !searchQuery || node.name.toLowerCase().includes(searchQuery.toLowerCase());
    const hasMatchingChildren = filteredChildren.length > 0;
    if (searchQuery && !matchesSearch && !hasMatchingChildren) {
      return null;
    }

    return (
      <div className={styles.treeNode} style={{ '--depth': depth }}>
        <div
          className={`${styles.nodeContent} ${isSelected ? styles.selected : ''} ${
            canSelect ? styles.selectable : styles.expandable
          }`}
          onClick={() => handleItemClick(node)}
        >
          {hasChildren && (
            <span
              className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.id, e);
              }}
            >
              â¶
            </span>
          )}
          <span className={styles.nodeLabel}>
            {node.name}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div className={styles.children}>
            {filteredChildren.map((child) => (
              <TreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const filteredTree = searchQuery
    ? katoTree.filter((node) => {
        const nodeMatches = node.name.toLowerCase().includes(searchQuery.toLowerCase());
        const hasMatchingDescendants = (n) => {
          if (n.name.toLowerCase().includes(searchQuery.toLowerCase())) return true;
          if (n.children) {
            return n.children.some(hasMatchingDescendants);
          }
          return false;
        };
        return nodeMatches || hasMatchingDescendants(node);
      })
    : katoTree;

  return (
    <div className={styles.selectKato} {...props}>
      <div className={styles.label}>
        {label || 'Ð ÐµÐ³Ð¸Ð¾Ð½ (ÐÐÐ¢Ð)'}
        {required && <span className={styles.required}>*</span>}
      </div>

      <div
        className={`${styles.input} ${error ? styles.error : ''} ${isOpen ? styles.open : ''}`}
        onClick={handleOpen}
      >
        <span className={styles.value}>{getDisplayText()}</span>
        <div className={styles.arrow}>
          <svg
            height="20"
            width="20"
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
          </svg>
        </div>
      </div>

      {isOpen && (
        <>
          <div className={styles.backdrop} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdown}>
            <div className={styles.searchBox}>
              <input
                type="text"
                placeholder="ÐÐ¾Ð¸ÑÐº..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className={styles.treeContainer}>
              {loading && <div className={styles.loading}>ÐÐ°Ð³ÑÑÐ·ÐºÐ°...</div>}
              {!loading && filteredTree.length === 0 && (
                <div className={styles.empty}>ÐÐ¸ÑÐµÐ³Ð¾ Ð½Ðµ Ð½Ð°Ð¹Ð´ÐµÐ½Ð¾</div>
              )}
              {!loading &&
                filteredTree.map((node) => <TreeNode key={node.id} node={node} depth={0} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectKato;
